'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState } from 'react'
import {
  LayoutDashboardIcon,
  LogOut,
  MailIcon,
  MapPinIcon,
  SettingsIcon,
  UserIcon,
  UserPenIcon,
  WrenchIcon,
} from 'lucide-react'

import { signOut } from '@/app/actions/auth'
import { Drawer } from '@/components/Drawer'
import { Media } from '@/components/Media'
import { Button } from '@/components/ui/button'
import { JOIN_PATH } from '@/constants/authRoutes'
import { useLenisContext } from '@/providers/Lenis'
import { usStates } from '@/utilities/states'
import { cn } from '@/utilities/ui'

import { ProfileProgress } from './ProfileProgress'
import { FormType, Settings } from './Settings'

import type { Media as MediaType, User } from '@/payload-types'

const Member = ({
  user,
  onUserUpdated,
}: {
  user: User
  onUserUpdated: (updatedUser?: User) => Promise<void>
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [openSettings, setOpenSettings] = useState(false)
  const [settingsFormType, setSettingsFormType] = useState<FormType>('profile')
  const pathname = usePathname()
  const isDashboard = pathname === '/dashboard'
  const { isMobile } = useLenisContext()

  const handleSignOut = async () => {
    setIsLoading(true)
    try {
      await signOut()
    } catch (error) {
      console.error('Error signing out:', error)
      setIsLoading(false)
    }
  }

  const handleOpenSettings = (formType: FormType = 'profile') => {
    setSettingsFormType(formType)
    setOpenSettings(true)
  }
  return (
    <div className="h-fit w-full font-family-body">
      <div className="flex flex-col items-center gap-2 pb-4">
        <div className="relative size-20 overflow-hidden rounded-full border border-border">
          {!user.image ? (
            <div className="flex size-full items-center justify-center rounded-full bg-neutral-200 object-cover object-center">
              <UserIcon className="size-12 self-center text-accent" />
            </div>
          ) : (
            <Media
              resource={user.image as MediaType}
              context={'thumbnail'}
              fill={true}
              imgClassName="size-full rounded-full object-cover object-center"
            />
          )}
        </div>
        <div>
          <h1 className="text-left font-body text-base font-semibold md:text-lg">
            {user.firstName ?? user.email} {user.firstName && user.lastName ? user.lastName : ''}
          </h1>
        </div>
      </div>
      <div className="flex flex-col items-center gap-1.5 border-b border-border px-2 pb-6 text-center">
        <p className="flex items-center justify-center gap-1.5 text-center font-body text-sm font-medium text-muted-foreground">
          <MailIcon className="size-3.5 text-muted-foreground" />
          {user.email}
        </p>
        {user.location?.location?.state && (
          <p className="flex items-center gap-1.5 text-left text-sm text-muted-foreground">
            <MapPinIcon className="size-3.5 text-muted-foreground" />{' '}
            {
              usStates.find(
                (state: { value: string }) => state.value === user.location?.location?.state,
              )?.label
            }
          </p>
        )}
        <p className="flex items-center gap-1.5 text-left text-sm text-muted-foreground">
          <UserIcon className="size-3.5 text-muted-foreground" /> Member since{' '}
          {new Date(user.createdAt).toLocaleDateString()}
        </p>
        <Button
          variant="accent"
          icon={true}
          customIcon={<UserPenIcon className="size-4.5" />}
          className="my-1 w-full"
          onClick={() => handleOpenSettings('profile')}
        >
          Edit Profile
        </Button>
        <ProfileProgress user={user} />
      </div>
      <ul className="flex flex-col gap-0.5 border-b border-border py-4">
        <li className="flex list-none items-center gap-2 font-medium text-muted-foreground">
          <Button
            variant="filter"
            icon={false}
            asChild
            className={cn(
              isDashboard &&
                'selected [&.selected]:bg-evergreen-100 [&.selected]:text-secondary-foreground',
            )}
          >
            <Link href="/dashboard">
              <span className="flex items-center gap-2">
                <LayoutDashboardIcon className="size-4.5" />
                Dashboard
              </span>
            </Link>
          </Button>
        </li>
        <li className="flex list-none items-center gap-2 font-medium text-muted-foreground">
          {user.role === 'admin' && (
            <Button variant="filter" icon={true} asChild>
              <Link href="/admin" target="_blank">
                <span className="flex items-center gap-2">
                  <WrenchIcon className="size-4.5" />
                  Admin Dashboard
                </span>
              </Link>
            </Button>
          )}
        </li>
        <li className="flex list-none items-center gap-2 font-medium text-muted-foreground">
          <Button variant="filter" icon={false} asChild>
            <Link href={JOIN_PATH}>
              <span className="flex items-center gap-2">
                <UserIcon className="size-4.5" />
                <span className="text-sm">Manage Membership</span>
              </span>
            </Link>
          </Button>
        </li>
        <li className="flex list-none items-center gap-2 font-medium text-muted-foreground">
          <Button variant="filter" icon={false} onClick={() => handleOpenSettings('password')}>
            <span className="flex items-center gap-2">
              <SettingsIcon className="size-4.5" />
              <span className="text-sm">Settings</span>
            </span>
          </Button>
        </li>
      </ul>
      <div className="my-4">
        <Button variant="filter" icon={true} onClick={handleSignOut}>
          <span className="flex items-center gap-2">
            <LogOut className="size-4.5" />
            <span className="text-sm">{isLoading ? 'Signing out...' : 'Sign out'}</span>
          </span>
        </Button>
      </div>
      <Drawer
        open={openSettings}
        setOpen={setOpenSettings}
        ariaTitle="Settings"
        fullWidth={isMobile}
      >
        <div className="relative flex h-full flex-col">
          <Settings
            key={settingsFormType}
            user={user}
            setOpenDrawer={setOpenSettings}
            setCurrentProfileStep={() => {}}
            onUserUpdated={onUserUpdated}
            initialFormType={settingsFormType}
          />
        </div>
      </Drawer>
    </div>
  )
}

export default Member
